import { h, clear, toast, formatDate, confirmModal } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Save, KeyRound, User, Info, Shield } from 'lucide';
import { AuthAPI } from '../services/endpoints.js';
import { CurrentUser } from '../services/api.js';
import { validateForm, showFieldErrors, required, email, minLen, maxLen, matches } from '../utils/validators.js';
import { Avatar, AVATAR_COLORS, DEFAULT_AVATAR_COLOR } from '../components/avatar.js';
import { SettingsLayout } from '../components/ui.js';

const profileRules = {
  full_name: [(v) => required(v, 'El nombre completo'), minLen(2, 'El nombre completo'), maxLen(100, 'El nombre completo')],
  email: [(v) => required(v, 'El correo electrónico')],
  document_number: [(v) => required(v, 'El número de documento')],
  phone: [(v) => required(v, 'El teléfono')],
};

const passwordRules = {
  currentPassword: [(v) => required(v, 'La contraseña actual')],
  newPassword: [(v) => required(v, 'La nueva contraseña'), minLen(6, 'La nueva contraseña')],
  confirmPassword: [(v, values) => required(v, 'La confirmación') || matches('newPassword', 'La confirmación no coincide')(v, values)],
};

export const ProfilePage = async (user) => {
  const view = document.getElementById('view');
  clear(view);

  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, 'Mi perfil'),
    h('span', { class: 'badge active' }, roleLabel(user.role)),
  ));

  let profile;
  try {
    const res = await AuthAPI.getProfile();
    profile = res.data;
  } catch (err) {
    toast(err.message, 'error');
    return;
  }

  const tabs = [
    { id: 'profile', label: 'Perfil' },
    { id: 'account', label: 'Cuenta' },
    { id: 'security', label: 'Seguridad' },
  ];

  let activeTab = 'profile';
  const settingsLayout = SettingsLayout({ tabs, activeTab, onTabChange });
  view.appendChild(settingsLayout);
  const content = settingsLayout.querySelector('.settings-content');

  function renderTab() {
    clear(content);
    if (activeTab === 'profile') renderProfile();
    else if (activeTab === 'account') renderAccount();
    else renderSecurity();
  }

  function renderProfile() {
    const origColor = profile.avatar_color || null;
    let selectedColor = origColor;

    const avatarPreview = Avatar(profile.full_name, selectedColor || DEFAULT_AVATAR_COLOR, 64);
    avatarPreview.classList.add('avatar--preview');

    const swatches = AVATAR_COLORS.map((c) => {
      const swatch = h('button', {
        class: 'color-swatch' + (c === selectedColor ? ' selected' : ''),
        style: `background: ${c}`,
        'data-color': c,
        type: 'button',
        onclick: () => selectColor(c),
      });
      return swatch;
    });

    async function selectColor(color) {
      const container = editCard.querySelector('.color-swatches');
      container.querySelectorAll('.color-swatch').forEach((s) => s.classList.toggle('selected', s.dataset.color === color));
      selectedColor = color;
      avatarPreview.style.setProperty('--avatar-bg', color);
      try {
        const res = await AuthAPI.updateProfile({ avatar_color: color });
        CurrentUser.set(res.data);
      } catch (err) {
        toast(err.message, 'error');
        selectedColor = origColor;
        avatarPreview.style.setProperty('--avatar-bg', origColor || DEFAULT_AVATAR_COLOR);
        container.querySelectorAll('.color-swatch').forEach((s) => s.classList.toggle('selected', s.dataset.color === origColor));
      }
    }

    const editCard = h('div', { class: 'form-create' },
      h('h3', {}, 'Editar perfil'),
      h('p', { class: 'muted text-sm' }, 'Actualiza tu información personal y el color de tu avatar.'),
      h('div', { class: 'form-section' },
        h('div', { class: 'profile-avatar-section' },
          avatarPreview,
          h('div', { class: 'color-picker' },
            h('label', {}, 'Color del avatar'),
            h('div', { class: 'color-swatches' }, ...swatches),
          ),
        ),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Nombre completo'),
            h('input', { name: 'full_name', value: profile.full_name || '', autocomplete: 'name', required: true }),
          ),
          h('div', {},
            h('label', {}, 'Correo electrónico'),
            h('input', { name: 'email', type: 'email', value: profile.email || '', autocomplete: 'email', required: true }),
          ),
        ),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Número de documento'),
            h('input', { name: 'document_number', value: profile.document_number || '', autocomplete: 'off', required: true }),
          ),
          h('div', {},
            h('label', {}, 'Teléfono'),
            h('input', { name: 'phone', type: 'tel', inputmode: 'numeric', value: profile.phone || '', autocomplete: 'tel', required: true }),
          ),
        ),
        h('label', {}, 'NIT (opcional)'),
        h('input', { name: 'nit', value: profile.nit || '', autocomplete: 'off' }),
        h('p', { class: 'muted text-sm mt-8' }, 'El rol y la sucursal solo pueden ser cambiados por un administrador.'),
      ),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: saveProfile }, h('span', { html: iconSvg(Save, 16) }), 'Guardar cambios'),
      ),
    );
    content.appendChild(editCard);

    async function saveProfile() {
      const values = {
        full_name: editCard.querySelector('[name="full_name"]').value.trim(),
        email: editCard.querySelector('[name="email"]').value.trim(),
        document_number: editCard.querySelector('[name="document_number"]').value.trim(),
        phone: editCard.querySelector('[name="phone"]').value.trim(),
      };
      const errors = validateForm(values, profileRules);
      if (Object.keys(errors).length) { showFieldErrors(editCard, errors); return; }
      const data = {
        full_name: values.full_name,
        email: values.email,
        document_number: values.document_number,
        nit: editCard.querySelector('[name="nit"]').value.trim() || null,
        phone: values.phone,
      };
      try {
        const res = await AuthAPI.updateProfile(data);
        CurrentUser.set(res.data);
        toast('Perfil actualizado', 'success');
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  function renderAccount() {
    const card = h('div', { class: 'form-create' },
      h('h3', {}, 'Información de la cuenta'),
      h('p', { class: 'muted text-sm' }, 'Datos de tu cuenta que no pueden editarse desde aquí.'),
      h('div', { class: 'form-section' },
        h('div', { class: 'grid grid-2' },
          detailField('Correo electrónico', profile.email),
          detailField('Rol', roleLabel(profile.role)),
          detailField('Sucursal', profile.branch_name || '—'),
          detailField('Estado', profile.is_active ? 'Activo' : 'Inactivo', profile.is_active ? 'badge active' : 'badge inactive'),
          detailField('Miembro desde', formatDate(profile.created_at)),
          detailField('Última actualización', formatDate(profile.updated_at)),
        ),
      ),
    );
    content.appendChild(card);
  }

  function renderSecurity() {
    const card = h('div', { class: 'form-create' },
      h('h3', {}, 'Cambiar contraseña'),
      h('p', { class: 'muted text-sm' }, 'Actualiza tu contraseña de acceso al sistema.'),
      h('div', { class: 'form-section' },
        h('label', {}, 'Contraseña actual'),
        h('input', { name: 'currentPassword', type: 'password', autocomplete: 'current-password', required: true }),
        h('label', {}, 'Nueva contraseña'),
        h('input', { name: 'newPassword', type: 'password', autocomplete: 'new-password', required: true }),
        h('label', {}, 'Confirmar nueva contraseña'),
        h('input', { name: 'confirmPassword', type: 'password', autocomplete: 'new-password', required: true }),
      ),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: changePassword }, h('span', { html: iconSvg(KeyRound, 16) }), 'Actualizar contraseña'),
      ),
    );
    content.appendChild(card);

    async function changePassword() {
      const confirmed = await confirmModal('¿Cambiar tu contraseña?', { confirmText: 'Actualizar', variant: 'success' });
      if (!confirmed) return;
      const values = {
        currentPassword: card.querySelector('[name="currentPassword"]').value,
        newPassword: card.querySelector('[name="newPassword"]').value,
        confirmPassword: card.querySelector('[name="confirmPassword"]').value,
      };
      const errors = validateForm(values, passwordRules);
      if (Object.keys(errors).length) { showFieldErrors(card, errors); return; }
      try {
        await AuthAPI.changePassword(values.currentPassword, values.newPassword);
        toast('Contraseña actualizada', 'success');
        card.querySelector('[name="currentPassword"]').value = '';
        card.querySelector('[name="newPassword"]').value = '';
        card.querySelector('[name="confirmPassword"]').value = '';
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  function onTabChange(id) {
    activeTab = id;
    renderTab();
  }

  renderTab();
};

const roleLabel = (r) => r === 'admin' ? 'administrador' : r === 'instructor' ? 'instructor' : 'beneficiario';

const detailField = (label, value, badgeClass) => h('div', { class: 'profile-field' },
  h('label', {}, label),
  badgeClass ? h('span', { class: badgeClass }, value) : h('div', { class: 'profile-value' }, value || '—'),
);
